/**
 * CheckoutForm - Stripe Checkout integration for donation processing
 *
 * This module handles payment collection for both one-time and recurring donations
 * using Stripe's Checkout API. It follows a two-component pattern:
 *
 * 1. CheckoutFormProviderWrap: Initializes Stripe and creates checkout session
 * 2. StripeCheckoutForm: Renders payment UI and handles confirmation
 *
 * Payment Flow:
 * - Creates Stripe checkout session with donation amount and frequency
 * - Displays Stripe-hosted payment elements (card, billing address)
 * - Confirms payment and handles success/error states
 * - Supports both immediate charges (one-time) and subscriptions (recurring)
 *
 * Integration Notes:
 * - Uses @stripe/react-stripe-js/checkout for embedded checkout
 * - Session creation includes isRecurring and donationFrequency for backend processing
 * - Email is pre-filled from donor info but editable if needed
 */
import { BillingAddressElement, CheckoutProvider, PaymentElement, useCheckout } from '@stripe/react-stripe-js/checkout';
import { loadStripe, StripeCheckoutConfirmResult } from '@stripe/stripe-js';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../models';
import { useEventLogging } from '../../../../../models/Logging';
import { useClientStripeSessionByToken } from '../../../../../queries';
import { Button } from '../../../../../web/components-v2/Button';
import { Input } from '../../../../../web/components-v2/Input';
import { LoadingLogo } from '../../../../../web/components/LoadingLogo';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { bs } from '../../../../../web/styles/styles';
import { asApiError, DonationFrequency, IDonationSurveyResponse } from '../../../../models/Api';
import { styleSheet } from './styles';

/**
/**
 * Type for Stripe checkout confirmation request
 * Email is optional and only included when user can edit it
 */

interface IProps extends IModalContext {
	stripePublicId: string;
	donationResponse: IDonationSurveyResponse;
	onRequestClose: () => void;
	token: string;
	donationAmount: number;
	serviceFeeAmount: number;
	donorCoversFees: boolean;
	isRecurring: boolean;
	donationFrequency?: DonationFrequency;
}

/**
 * Type for Stripe checkout confirmation request
 * Email is optional and only included when user can edit it
 */
type ConfirmRequest = {
	email?: string;
};

/**
 * Error messages used throughout the checkout form
 */
const ERROR_MESSAGES = {
	CHECKOUT_NOT_READY: 'Payment processing failed. Please try again.',
	INVALID_EMAIL: 'Please enter a valid email address.',
	GENERIC_ERROR: 'Oops! Something went wrong. Please try again.',
} as const;

/**
 * Timeout duration for Stripe Elements to fully initialize before hiding loading indicator
 */
const STRIPE_ELEMENTS_LOAD_DELAY_MS = 1000;

/**
 * Formats an amount in cents to a dollar string
 * @param cents - Amount in cents (e.g., 1299)
 * @returns Formatted dollar string (e.g., "12.99")
 */
const formatCentsAsDollars = (cents: number): string => {
	return (cents / 100).toFixed(2);
};

/**
 * Wrapper component that initializes Stripe checkout session and provides
 * the Stripe context to the checkout form.
 *
 * This component handles the async initialization of Stripe and creates
 * a checkout session on the server before rendering the payment form.
 *
 * @param stripePublicId - Stripe publishable key for the account
 * @param donationResponse - The donor's survey response data
 * @param onRequestClose - Callback fired when checkout succeeds
 * @param parentModal - Modal context for managing modal state
 * @param token - Session token for initializing the Stripe session
 * @param donationAmount - Donation amount in cents
 * @param serviceFeeAmount - Service fee amount in cents (if donor covers fees)
 * @param donorCoversFees - Whether the donor elected to cover service fees
 */
export const CheckoutFormProviderWrap = ({
	stripePublicId,
	donationResponse,
	onRequestClose,
	parentModal,
	token,
	donationAmount,
	serviceFeeAmount,
	donorCoversFees,
	isRecurring,
	donationFrequency,
}: IProps) => {
	const stripeSession = useClientStripeSessionByToken();
	const stripePromise = React.useRef(() => {
		if (stripePublicId) {
			const sp = loadStripe(stripePublicId);
			return sp;
		}
		return null;
	}).current();

	const clientSecretPromise = React.useMemo(() => {
		return stripeSession.mutateAsync({
			token,
			donationAmount,
			serviceFeeAmount,
			donorCoversFees,
			response: donationResponse,
			isRecurring,
			donationFrequency,
			_type: 'DonationSurveyCheckout',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!clientSecretPromise) {
		return null;
	}

	return (
		<CheckoutProvider
			stripe={stripePromise}
			options={{
				clientSecret: clientSecretPromise,
			}}
		>
			<StripeCheckoutForm
				onSuccessClose={onRequestClose}
				onCloseModal={parentModal.onRequestClose}
				donorEmail={donationResponse?.donor?.email}
				donationAmount={donationAmount}
				serviceFeeAmount={serviceFeeAmount}
				donorCoversFees={donorCoversFees}
			/>
		</CheckoutProvider>
	);
};

export const CheckoutForm = inject(ModalChildComponentContextKey)(CheckoutFormProviderWrap);

/**
 * Stripe checkout form component that handles payment collection for donations.
 *
 * This component manages the entire payment flow including form validation,
 * submission, error handling, and success navigation. It integrates with
 * Stripe's Payment and Billing Address elements.
 *
 * Features:
 * - Conditionally editable email field based on Stripe session state
 * - Service fee display when donor covers fees
 * - Stripe Payment and Billing Address elements
 * - Structured error logging via useEventLogging
 * - Loading states for Stripe Elements initialization
 *
 * @param onSuccessClose - Callback to close modal on successful payment
 * @param onCloseModal - Callback to close modal with session ID
 * @param donorEmail - Pre-filled donor email from survey response
 * @param donationAmount - Donation amount in cents
 * @param serviceFeeAmount - Service fee in cents
 * @param donorCoversFees - Whether donor elected to cover fees
 */
export const StripeCheckoutForm = ({
	onSuccessClose,
	onCloseModal,
	donorEmail,
	donationAmount,
	serviceFeeAmount,
	donorCoversFees,
}: {
	onSuccessClose: () => void;
	onCloseModal: (sessionId: string) => void;
	donorEmail: string;
	donationAmount: number;
	serviceFeeAmount: number;
	donorCoversFees: boolean;
}) => {
	const checkout = useCheckout();
	const { logApiError } = useEventLogging('DonationSurveyCheckoutForm');
	let emailEditable = true;

	if (checkout?.type === 'success') {
		emailEditable = checkout?.checkout.email === null;
	}

	const [isLoading, setIsLoading] = React.useState(false);
	const [isScriptLoading, setIsScriptLoading] = React.useState(true);
	const [email, setEmail] = React.useState<string>(donorEmail || '');

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const location = useLocation();
	// Email is editable if Stripe hasn't already collected it from the donor.
	// When checkout.email is null, Stripe expects us to provide it in the confirmation request.
	// When checkout.email has a value, Stripe already has it (e.g., from a saved payment method)
	// and we should not override it.
	React.useEffect(() => {
		// Close modal when URL search params change (typically indicates Stripe redirect back)
		// This handles the case where Stripe redirects to a success/cancel URL with query params
		if (location.search) {
			onSuccessClose();
		}
	}, [location.search, onSuccessClose]);

	React.useEffect(() => {
		let timeout: NodeJS.Timeout | null = null;
		if (checkout?.type !== 'success') {
			return;
		}

		// Allow time for Stripe Elements to fully initialize before hiding loading indicator
		timeout = setTimeout(() => {
			setIsScriptLoading(false);
		}, STRIPE_ELEMENTS_LOAD_DELAY_MS);

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [checkout?.type]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.
		event.preventDefault();
		event.stopPropagation(); // Prevent the event from bubbling up
		// Prevent the form from being submitted again while processing
		// the payment
		setIsLoading(true);
		setErrorMessage(null);

		if (checkout?.type !== 'success') {
			setErrorMessage(ERROR_MESSAGES.CHECKOUT_NOT_READY);
			setIsLoading(false);
			return;
		}
		// Basic email validation - full validation happens server-side via Stripe
		if (!email?.includes('@')) {
			setErrorMessage(ERROR_MESSAGES.INVALID_EMAIL);
			setIsLoading(false);
			return;
		}

		try {
			const confirmRequest: ConfirmRequest = {};

			if (emailEditable) {
				// Use the email entered by the user
				confirmRequest.email = email;
			}

			const result: StripeCheckoutConfirmResult = await checkout.checkout.confirm(confirmRequest);

			if (result.type === 'error') {
				setErrorMessage(result.error.message);
				setIsLoading(false);

				return;
			}
			if (result.type === 'success') {
				setIsLoading(false);
				onSuccessClose();
			}
		} catch (error) {
			logApiError('DonationSurveyCheckoutFormError', asApiError(error));
			setErrorMessage(ERROR_MESSAGES.GENERIC_ERROR);
			setIsLoading(false);
		}
	};

	const totalAmount = donorCoversFees ? donationAmount + serviceFeeAmount : donationAmount;

	return (
		<div className={css(styleSheet.formContainer)}>
			<header className={css(styleSheet.header)}>
				<Button
					type='button'
					variant='link'
					onClick={() => {
						// Always allow closing the modal, but only pass session ID if checkout succeeded
						if (checkout.type === 'success') {
							onCloseModal(checkout.checkout.id);
						} else {
							// For error/loading states, close without session ID
							onCloseModal('');
						}
					}}
					styles={[styleSheet.closeButton]}
				>
					<XIcon />
				</Button>
			</header>
			{
				<form onSubmit={handleSubmit}>
					<section className={css(bs.mb2, bs.pr1)}>
						<h3 className={css(styleSheet.paymentInfoTitle)}>Your Donation</h3>

						<div className={css(styleSheet.orderInfo)}>
							<p>
								Donation Amount: <strong>${formatCentsAsDollars(donationAmount)}</strong>
							</p>
							{donorCoversFees && (
								<p>
									Service Fee: <strong>${formatCentsAsDollars(serviceFeeAmount)}</strong>
								</p>
							)}
							<p>
								Total: <strong>${formatCentsAsDollars(totalAmount)}</strong>
							</p>
						</div>
					</section>
					<div className={css(styleSheet.emailInput)}>
						<label htmlFor='email' className={css(styleSheet.label)}>
							Email
						</label>
						<Input id='email' type='email' onChange={e => setEmail(e.target.value)} value={email} placeholder='Email' />
					</div>
					{isScriptLoading ? (
						<div className={css(bs.flex, bs.flexCenter)}>
							<LoadingLogo size={50} />
						</div>
					) : null}
					<PaymentElement id='payment-element' />
					<BillingAddressElement />

					<Button
						type='submit'
						disabled={isLoading}
						variant='primary'
						size='default'
						isLoading={isLoading}
						styles={[styleSheet.submitButton]}
					>
						Donate ${formatCentsAsDollars(totalAmount)}
					</Button>
					{errorMessage && <small className={css(styleSheet.errorMessage)}>{errorMessage}</small>}
				</form>
			}
		</div>
	);
};
